import React, { useState, useEffect, useRef, useCallback } from "react";
import CloseIcon from "../../../../../../assets/svg/icons/common/CloseIcon";
import Modal from "../../../../../../components/modal";
import FilePreviewRow from "./FilePreviewRow";
import ButtonIcon from "../../../../../../components/ButtonIcon/ButtonIcon";
import styles from "./style.module.scss";
import useDomainStore from "../../../../../../stores/domain";

/**
 * Modal component for selecting document types for uploaded files
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal visibility state
 * @param {Function} props.onClose - Modal close handler
 * @param {Array<Object>} props.files - Array of files to process
 * @param {Function} props.onSubmit - Submit handler for file types
 * @returns {React.ReactElement} FileTypeModal component
 */
const FileTypeModal = ({ isOpen, onClose, files, onSubmit }) => {
  const { isKurvDomain } = useDomainStore();

  // State to store file type selections
  const [fileTypes, setFileTypes] = useState({});
  // State to store files currently pending classification
  const [pendingFiles, setPendingFiles] = useState(files);
  const [isLoading, setIsLoading] = useState(false);
  // Ref to track component mount state for async operations
  const isMounted = useRef(true);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialize file types when files prop changes
  useEffect(() => {
    if (isMounted.current) {
      const initialTypes = {};
      files.forEach((file) => {
        initialTypes[file.name] = "";
      });
      setFileTypes(initialTypes);
      setPendingFiles(files);
    }
  }, [files]);

  /**
   * Update file type for a specific file
   * @param {string} fileName - Name of the file to update
   * @param {string} type - Selected document type
   */
  const handleTypeChange = useCallback((fileName, type) => {
    if (isMounted.current) {
      setFileTypes((prev) => ({
        ...prev,
        [fileName]: type,
      }));
    }
  }, []);

  /**
   * Submit file types and close modal
   */
  const handleSubmit = useCallback(() => {
    setIsLoading(true);
    onSubmit(fileTypes);
  }, [fileTypes, onSubmit]);

  /**
   * Close modal without saving file types
   */
  const handleRemoveFiles = useCallback(() => {
    onClose();
  }, [onClose]);

  /**
   * Remove a single file from the pending files list
   * @param {string} fileName - Name of the file to remove
   */
  const handleRemoveFile = useCallback((fileName) => {
    if (isMounted.current) {
      setPendingFiles((prev) => {
        const newPendingFiles = prev.filter((file) => file.name !== fileName);

        // If this was the last file, close the modal
        if (newPendingFiles.length === 0) {
          onClose();
        }

        return newPendingFiles;
      });

      setFileTypes((prev) => {
        const newTypes = { ...prev };
        delete newTypes[fileName];
        return newTypes;
      });
    }
  }, [onClose]);

  /**
   * Check if any files are missing type selection
   */
  const isSubmitDisabled =
    Object.values(fileTypes).some((type) => !type) || pendingFiles.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      size="medium"
      onClose={onClose}
      notCentered={true}
      className={`${styles.modal} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      <div className={styles.modalBody}>
        <h3 className={styles.modalTitle}>Select what type of file uploaded</h3>
        <div className={styles.fileList}>
          {pendingFiles.map((file, index) => (
            <FilePreviewRow
              key={index}
              file={file}
              selectedType={fileTypes[file.name]}
              onTypeChange={handleTypeChange}
              onRemove={handleRemoveFile}
              isKurvDomain={isKurvDomain}
            />
          ))}
        </div>
      </div>

      <div className={styles.modalFooter}>
        <div className={styles.buttonGroup}>
          <ButtonIcon
            textColor={isKurvDomain ? "#22a276" : "#254FE2"}
            onClick={handleRemoveFiles}
            type="button"
            aria-label="Remove all files"
          >
            Remove files
          </ButtonIcon>
          <ButtonIcon
            bgColor={isKurvDomain ? "#22a276" : "#4c70ef"}
            textColor="#fff"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            type="button"
            aria-label="Submit file types"
            isLoading={isLoading}
          >
            Submit
          </ButtonIcon>
        </div>
      </div>
    </Modal>
  );
};

export default FileTypeModal;
